import { loadSwiper } from './swiper-loader.js';

if (!customElements.get('header-bar')) {
    const FOCUSABLE_SELECTOR = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const SCROLL_LOCK_CLASS = 'overflow-hidden';

    // Guards against a missed transitionend leaving a panel stuck in its closing state.
    const TRANSITION_BUFFER = 50;

    // Brief delay before closing so the pointer can cross the nav bar dead zone
    // between a trigger and its dropdown without the panel dismissing.
    const DESKTOP_CLOSE_DELAY = 150;

    // First-open only. Filters fly-bys across the bar without slowing swaps
    // between menus that are already open, or keyboard focus.
    const DESKTOP_OPEN_DELAY = 200;

    class HeaderBar extends HTMLElement {

        constructor() {
            super();
            this.toggle = null;
            this.menu = null;
            this.previousFocus = null;
            this.inertedSiblings = [];
            this.activeNested = null;
            this.activeNestedTrigger = null;
            this.nestedInertedSiblings = [];
            this.activeDesktopItem = null;
            this.closingPanels = new Map();
            this.panelHeights = new WeakMap();
            this.panelResizeObserver = null;
            this.desktopCloseTimer = null;
            this.desktopOpenTimer = null;
            this.pendingDesktopItem = null;
            this.desktopFocusLock = false;
            this.elCtaCarousels = null;
            this.ctaCarousels = [];
            this.elMobileLinksCarousel = null;
            this.elSearchSuggestionsCarousels = null;
            this.searchSuggestionsCarousels = [];
            this.searchResizeObserver = null;
            this.utilitiesResizeObserver = null;
            this.initializedCarousels = new WeakSet();
            this.mobileLinksCarouselInitialized = false;
            this.touchQuery = window.matchMedia('(max-width: 1023px)');

            this.handleToggleClick = this.handleToggleClick.bind(this);
            this.handleCloseClick = this.handleCloseClick.bind(this);
            this.handleNestedClick = this.handleNestedClick.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);
            this.handleQueryChange = this.handleQueryChange.bind(this);
            this.handleDocumentClick = this.handleDocumentClick.bind(this);
            this.handlePanelReveal = this.handlePanelReveal.bind(this);
            this.handleDesktopPointerOver = this.handleDesktopPointerOver.bind(this);
            this.handleDesktopPointerLeave = this.handleDesktopPointerLeave.bind(this);
            this.handleDesktopFocusIn = this.handleDesktopFocusIn.bind(this);
            this.handleDesktopFocusOut = this.handleDesktopFocusOut.bind(this);
        }

        connectedCallback() {
            this.toggle = this.querySelector('[data-menu-toggle]');
            this.menu = this.querySelector('[data-menu]');
            this.elCtaCarousels = this.querySelectorAll('[data-link-cta]');
            this.elMobileLinksCarousel = this.querySelector('[data-mobile-links]');
            this.elSearchSuggestionsCarousels = this.querySelectorAll('[data-search-suggestions]');

            if (!this.toggle || !this.menu) return;

            this.#resetMenuState();

            this.toggle.addEventListener('click', this.handleToggleClick);
            this.menu.addEventListener('click', this.handleNestedClick);
            this.touchQuery.addEventListener('change', this.handleQueryChange);
            this.menu.addEventListener('mouseenter', this.handlePanelReveal, true);
            this.menu.addEventListener('focusin', this.handlePanelReveal);
            this.menu.addEventListener('pointerover', this.handleDesktopPointerOver);
            this.menu.addEventListener('pointerleave', this.handleDesktopPointerLeave);
            this.menu.addEventListener('focusin', this.handleDesktopFocusIn);
            this.menu.addEventListener('focusout', this.handleDesktopFocusOut);

            this.#bindSearchTriggers();
            this.#applyMenuMode(this.touchQuery.matches);
            this.#syncUtilitiesLayout();
        }

        disconnectedCallback() {
            this.toggle?.removeEventListener('click', this.handleToggleClick);
            this.menu?.removeEventListener('click', this.handleNestedClick);
            this.touchQuery.removeEventListener('change', this.handleQueryChange);
            this.menu?.removeEventListener('mouseenter', this.handlePanelReveal, true);
            this.menu?.removeEventListener('focusin', this.handlePanelReveal);
            this.menu?.removeEventListener('pointerover', this.handleDesktopPointerOver);
            this.menu?.removeEventListener('pointerleave', this.handleDesktopPointerLeave);
            this.menu?.removeEventListener('focusin', this.handleDesktopFocusIn);
            this.menu?.removeEventListener('focusout', this.handleDesktopFocusOut);
            document.removeEventListener('click', this.handleDocumentClick);
            this.#disconnectUtilitiesResizeObserver();
            this.#disconnectPanelResizeObserver();
            this.#resetDesktopPanels();
            this.#restoreCompactMenu();
            this.#removeKeyHandler();
            this.#unlockScroll();
            this.#restoreNestedSiblings();
            this.#restoreSiblings();
        }

        handleToggleClick() {
            if (this.#isOpen()) {
                this.#close();
            } else {
                this.#open();
            }
        }

        handleCloseClick() {
            this.#close();
        }

        closeMenu(options = {}) {
            this.#close(options);
        }

        resetMenuState() {
            this.#resetMenuState();
        }

        handleQueryChange(event) {
            this.#applyMenuMode(event.matches);
        }

        handleDocumentClick(event) {
            if (!this.activeNested) return;

            const triggers = this.#triggersForPanel(this.activeNested);
            const usesClickToggle = Array.from(triggers).some((trigger) => trigger.hasAttribute('data-link-toggle-click'));

            if (!usesClickToggle) return;
            if (this.activeNested.contains(event.target)) return;
            if (Array.from(triggers).some((trigger) => trigger.contains(event.target))) return;

            this.#closeNestedDialog(this.activeNested);
        }

        handleNestedClick(event) {
            const trigger = event.target.closest('[data-link-toggle]');

            if (trigger && this.menu.contains(trigger)) {
                if (this.touchQuery.matches || trigger.hasAttribute('data-link-toggle-click')) {
                    this.#toggleNestedDialog(trigger);
                }
                return;
            }

            const back = event.target.closest('[data-link-back]');

            if (back && this.menu.contains(back)) {
                const panel = back.closest('[data-link-menu]');
                this.#closeNestedDialog(panel);
            }
        }

        handleDesktopPointerOver(event) {
            if (!this.#isDesktopNav()) return;
            if (this.#isTransitionalTarget(event.target)) return;

            const item = this.#desktopItemFor(event.target);

            if (item) {
                this.#clearDesktopCloseTimer();
                this.#requestDesktopPanelFromPointer(item);
                return;
            }

            this.#clearDesktopOpenTimer();

            if (this.#isHovered(this.activeDesktopItem)) {
                this.#clearDesktopCloseTimer();
                return;
            }

            const activePanel = this.#activeDesktopPanel();
            if (activePanel?.contains(event.target)) {
                this.#clearDesktopCloseTimer();
                return;
            }

            if (event.target.closest('.header-menu--link-single, .header-menu--navigation-utility')) {
                this.#clearDesktopCloseTimer();
                this.#requestDesktopPanel(null);
                return;
            }

            if (this.activeDesktopItem) {
                this.#scheduleDesktopPanelClose();
            }
        }

        handleDesktopPointerLeave(event) {
            if (!this.#isDesktopNav()) return;

            const related = event.relatedTarget;
            if (related instanceof Element) {
                if (this.#desktopItemFor(related)) {
                    this.#clearDesktopCloseTimer();
                    return;
                }

                if (this.#activeDesktopPanel()?.contains(related)) {
                    this.#clearDesktopCloseTimer();
                    return;
                }
            }

            this.#clearDesktopOpenTimer();
            this.#scheduleDesktopPanelClose();
        }

        handleDesktopFocusIn(event) {
            if (!this.#isDesktopNav() || this.desktopFocusLock) return;
            if (this.#isTransitionalTarget(event.target)) return;

            const item = this.#desktopItemFor(event.target);

            // Focus landing elsewhere in the bar shouldn't pull the panel away from a
            // buyer who is still hovering it.
            if (!item && this.#isHovered(this.activeDesktopItem)) return;

            this.#requestDesktopPanel(item);
        }

        handleDesktopFocusOut(event) {
            if (!this.#isDesktopNav()) return;
            if (event.relatedTarget instanceof Node && this.menu.contains(event.relatedTarget)) return;
            if (this.#isHovered(this.activeDesktopItem)) return;

            this.#requestDesktopPanel(null);
        }

        handleKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (this.activeNested) {
                    this.#closeNestedDialog(this.activeNested);
                } else {
                    this.#close();
                }
                return;
            }

            if (event.key === 'Tab') {
                this.#trapFocus(event);
            }
        }


        #applyMenuMode(isTouch) {
            if (!this.menu) return;

            this.#resetDesktopPanels();

            if (isTouch) {
                this.menu.setAttribute('role', 'dialog');
                // Header chrome (logo, country, cart, menu toggle) stays operable while open.
                this.menu.setAttribute('aria-modal', 'false');
            } else {
                this.#restoreCompactMenu();

                if (this.activeNested) {
                    this.#closeNestedDialog(this.activeNested, { skipFocusReturn: true });
                }
                if (this.#isOpen()) this.#close({ skipFocusReturn: true });

                this.menu.removeAttribute('role');
                this.menu.removeAttribute('aria-modal');
                this.menu.removeAttribute('data-menu-state');
            }

            for (const trigger of this.menu.querySelectorAll('[data-link-toggle]')) {
                this.#resetPanel(trigger);
            }

            this.#syncUtilitiesLayout();
        }

        #syncUtilitiesLayout() {
            this.#disconnectUtilitiesResizeObserver();

            if (this.touchQuery.matches) {
                this.style.removeProperty('--header-bar-utilities-size');
                return;
            }

            const utilities = this.querySelector('.header-bar--utilities');
            if (!utilities) return;

            const syncSize = () => {
                this.style.setProperty('--header-bar-utilities-size', `${utilities.offsetWidth}px`);
            };

            syncSize();
            this.utilitiesResizeObserver = new ResizeObserver(syncSize);
            this.utilitiesResizeObserver.observe(utilities);
        }

        #disconnectUtilitiesResizeObserver() {
            this.utilitiesResizeObserver?.disconnect();
            this.utilitiesResizeObserver = null;
        }

        #isOpen() {
            return this.menu?.getAttribute('data-menu-state') === 'open';
        }

        #resetMenuState() {
            if (!this.menu || !this.toggle) return;

            this.menu.setAttribute('data-menu-suppressed', '');

            for (const panel of [...this.closingPanels.keys()]) {
                this.#cancelClosingCleanup(panel);
            }

            this.#clearDesktopCloseTimer();
            this.#clearDesktopOpenTimer();
            this.toggle.classList.remove('active');
            this.menu.removeAttribute('data-menu-state');
            this.toggle.setAttribute('aria-expanded', 'false');

            const openLabel = this.toggle.dataset.labelOpen;
            if (openLabel) this.toggle.setAttribute('aria-label', openLabel);

            for (const panel of this.menu.querySelectorAll('[data-link-menu]')) {
                panel.removeAttribute('data-nested-state');
                panel.removeAttribute('data-nested-swap');
            }

            for (const trigger of this.menu.querySelectorAll('[data-link-toggle][aria-expanded="true"]')) {
                trigger.setAttribute('aria-expanded', 'false');
            }

            this.activeNested = null;
            this.activeNestedTrigger = null;
            this.activeDesktopItem = null;
            this.previousFocus = null;
            this.desktopFocusLock = false;

            this.#restoreCompactMenu();
            this.#restoreNestedSiblings();
            this.#restoreSiblings();
            this.#removeKeyHandler();
            document.removeEventListener('click', this.handleDocumentClick);
            document.body.classList.remove(SCROLL_LOCK_CLASS);
            this.style.removeProperty('--header-menu-sheet-height');

            requestAnimationFrame(() => {
                this.menu?.removeAttribute('data-menu-suppressed');
            });
        }

        #open() {
            if (!this.menu || !this.toggle) return;

            this.menu.removeAttribute('data-menu-suppressed');
            this.previousFocus = document.activeElement;

            this.#syncMenuOffset();

            this.toggle.classList.add('active');
            this.menu.setAttribute('data-menu-state', 'open');
            this.toggle.setAttribute('aria-expanded', 'true');

            const closeLabel = this.toggle.dataset.labelClose;
            if (closeLabel) this.toggle.setAttribute('aria-label', closeLabel);

            this.#inertSiblings();
            this.#lockScroll();
            this.#installKeyHandler();
            this.#initMobileLinksCarousel();
            this.#focusFirstElement();
            this.#notifyBlockingUi();
        }

        #close({ skipFocusReturn = false } = {}) {
            if (!this.menu || !this.toggle) return;
            if (!this.#isOpen()) return;

            this.toggle.classList.remove('active');
            this.menu.removeAttribute('data-menu-state');
            this.toggle.setAttribute('aria-expanded', 'false');

            const openLabel = this.toggle.dataset.labelOpen;
            if (openLabel) this.toggle.setAttribute('aria-label', openLabel);

            this.#restoreSiblings();
            this.#unlockScroll();
            this.#removeKeyHandler();
            this.#notifyBlockingUi();

            const triggers = this.menu.querySelectorAll('[data-link-toggle]');
            for (const trigger of triggers) {
                this.#resetPanel(trigger);
            }

            if (!skipFocusReturn && this.previousFocus instanceof HTMLElement) {
                this.previousFocus.focus({ preventScroll: true });
            }

            this.previousFocus = null;
            this.ctaCarousels.forEach(swiper => swiper.slideTo(0, false));

            if (this.activeNested) {

                this.#closeNestedDialog(this.activeNested, { skipFocusReturn: true });
            }

            this.#restoreCompactMenu();
        }

        #installKeyHandler() {
            document.addEventListener('keydown', this.handleKeydown);
        }

        #removeKeyHandler() {
            document.removeEventListener('keydown', this.handleKeydown);
        }

        #focusFirstElement() {
            const focusables = this.#getFocusables();
            const target = focusables[0] ?? this.menu;
            target?.focus({ preventScroll: true });
        }

        #trapFocus(event) {
            const focusables = this.activeNested
                ? this.#getNestedFocusables(this.activeNested)
                : this.#getTrapFocusables();

            if (focusables.length === 0) {
                event.preventDefault();
                return;
            }

            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;

            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus({ preventScroll: true });
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus({ preventScroll: true });
            } else if (!focusables.includes(active)) {
                event.preventDefault();
                first.focus({ preventScroll: true });
            }
        }

        #getHeaderChrome() {
            return [
                this.toggle,
                this.menu,
                this.querySelector('[data-header-link]'),
                this.querySelector('.header-bar--utilities'),
            ].filter(Boolean);
        }

        #getHeaderChromeFocusables() {
            const elUtilities = this.querySelector('.header-bar--utilities');
            const utilityFocusables = elUtilities
                ? Array.from(elUtilities.querySelectorAll(FOCUSABLE_SELECTOR))
                : [];

            return [
                this.toggle,
                this.querySelector('[data-header-link]'),
                ...utilityFocusables,
            ].filter(isVisible);
        }

        #getTrapFocusables() {
            if (!this.menu) return [];

            const menuFocusables = Array.from(this.menu.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);

            if (this.#isDesktopNav()) {
                return menuFocusables;
            }

            return [...this.#getHeaderChromeFocusables(), ...menuFocusables];
        }

        #getFocusables() {
            if (!this.menu) return [];

            const candidates = this.menu.querySelectorAll(FOCUSABLE_SELECTOR);
            return Array.from(candidates).filter(isVisible);
        }

        #getNestedFocusables(panel) {
            const candidates = panel.querySelectorAll(FOCUSABLE_SELECTOR);
            return Array.from(candidates).filter(isVisible);
        }

        #notifyBlockingUi() {
            document.dispatchEvent(new CustomEvent('modal-stack:change', {
                detail: {
                    hasOpenModal: typeof window.PopupModal !== 'undefined' && window.PopupModal.isBlockingUiOpen(),
                },
            }));
        }

        #inertSiblings() {
            const allowed = this.#getHeaderChrome();
            if (allowed.length === 0) return;

            const ancestors = this.#collectAncestors(allowed, document.body);

            this.inertedSiblings = [];
            this.#walkAndIsolate(document.body, allowed, ancestors, this.inertedSiblings);
        }

        #inertNestedSiblings(panel) {
            if (!panel || !this.menu) return;

            const ancestors = this.#collectAncestors([panel], this.menu);
            ancestors.add(this.menu);

            this.nestedInertedSiblings = [];
            this.#walkAndIsolate(this.menu, [panel], ancestors, this.nestedInertedSiblings);
        }

        #collectAncestors(nodes, boundary) {
            const ancestors = new Set();

            for (const node of nodes) {
                let current = node.parentElement;
                while (current && current !== boundary) {
                    ancestors.add(current);
                    current = current.parentElement;
                }
            }

            return ancestors;
        }

        #walkAndIsolate(parent, allowed, ancestors, store) {
            for (const child of parent.children) {
                if (allowed.includes(child)) continue;
                if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') continue;

                if (ancestors.has(child)) {
                    this.#walkAndIsolate(child, allowed, ancestors, store);
                } else {
                    const wasInert = child.hasAttribute('inert');
                    if (!wasInert) child.setAttribute('inert', '');
                    store.push({ element: child, wasInert });
                }
            }
        }

        #restoreSiblings() {
            for (const entry of this.inertedSiblings) {
                if (!entry.wasInert) entry.element.removeAttribute('inert');
            }
            this.inertedSiblings = [];
        }

        #restoreNestedSiblings() {
            for (const entry of this.nestedInertedSiblings) {
                if (!entry.wasInert) entry.element.removeAttribute('inert');
            }
            this.nestedInertedSiblings = [];
        }

        #syncMenuOffset() {
            if (!this.menu) return;

            // The header-bar is sticky, so its measured top already accounts for
            // however much of the promo bar is still visible at this scroll
            // position. Read it once on open (scroll is locked while the menu is
            // open) rather than tracking scroll continuously.
            const top = Math.max(0, Math.round(this.getBoundingClientRect().top));
            this.menu.style.setProperty('--header-menu-top', `${top}px`);
        }

        #shouldLockScroll() {
            if (this.#isOpen()) return true;

            // Hold the lock until the outgoing panel has finished animating out, so the
            // page cannot scroll away from under a sheet that is still on screen.
            if (!this.touchQuery.matches && (this.activeDesktopItem || this.closingPanels.size > 0)) {
                return true;
            }

            return false;
        }

        #syncScrollLock() {
            document.body.classList.toggle(SCROLL_LOCK_CLASS, this.#shouldLockScroll());
        }

        #lockScroll() {
            this.#syncScrollLock();
        }

        #unlockScroll() {
            this.#syncScrollLock();
        }

        #toggleNestedDialog(trigger) {
            const panel = this.#panelForTrigger(trigger);
            if (!panel) return;

            this.activeNested === panel ? this.#closeNestedDialog(panel) : this.#openNestedDialog(trigger);
        }

        #openNestedDialog(trigger) {
            const panel = this.#panelForTrigger(trigger);
            if (!panel) return;

            this.#requestDesktopPanel(null);

            if (this.activeNested && this.activeNested !== panel) {
                this.#closeNestedDialog(this.activeNested, { skipFocusReturn: true });
            }

            for (const nestedTrigger of this.#triggersForPanel(panel)) {
                nestedTrigger.setAttribute('aria-expanded', nestedTrigger === trigger ? 'true' : 'false');
            }
            panel.setAttribute('data-nested-state', 'open');
            panel.setAttribute('role', 'dialog');
            panel.setAttribute('aria-modal', 'true');

            this.activeNested = panel;
            this.activeNestedTrigger = trigger;

            // iOS only opens the keyboard when focus() runs in the same turn as
            // the tap. Focus before inerting the trigger (which is still focused
            // from the tap), then again after in case inert moved focus.
            if (this.#isSearchPanel(panel)) {
                this.#focusSearchInput(panel, { allowRetry: false });
            }

            this.#inertNestedSiblings(panel);

            if (this.#isSearchPanel(panel)) {
                this.#focusSearchInput(panel);
            }

            this.#installKeyHandler();

            if (trigger.hasAttribute('data-link-toggle-click')) {
                setTimeout(() => {
                    document.addEventListener('click', this.handleDocumentClick);
                }, 0);
            }

            if (this.#isSearchPanel(panel)) {
                this.#initCarouselsInPanel(panel);

                requestAnimationFrame(() => {
                    this.#updateSearchSuggestionsCarousels();

                    if (this.touchQuery.matches) {
                        this.#updateCompactMenuHeight(panel);
                        this.#observeSearchPanelResize(panel);
                    }
                });

                return;
            }

            this.#initCarouselsInPanel(panel);

            const focusables = this.#getNestedFocusables(panel);
            const target = focusables[0] ?? panel;
            target?.focus({ preventScroll: true });
        }

        #closeNestedDialog(panel, { skipFocusReturn = false } = {}) {
            if (!panel || this.activeNested !== panel) return;

            const trigger = this.activeNestedTrigger ?? this.#triggerForPanel(panel);

            if (this.#isSearchPanel(panel)) {
                this.#restoreCompactMenu();
            }

            panel.removeAttribute('data-nested-state');
            panel.removeAttribute('role');
            panel.removeAttribute('aria-modal');
            for (const nestedTrigger of this.#triggersForPanel(panel)) {
                nestedTrigger.setAttribute('aria-expanded', 'false');
            }

            // const elNav = this.activeNested.querySelector('.header-menu--link-navigation');
            // elNav && (elNav.scrollTop = 0);

            this.#restoreNestedSiblings();
            this.activeNested = null;
            this.activeNestedTrigger = null;

            document.removeEventListener('click', this.handleDocumentClick);

            if (!this.#isOpen()) {
                this.#removeKeyHandler();
            }

            if (!skipFocusReturn && trigger instanceof HTMLElement) {
                trigger.focus({ preventScroll: true });
            }
        }

        #resetPanel(trigger) {
            const panel = this.#panelForTrigger(trigger);
            if (!panel) return;

            trigger.setAttribute('aria-expanded', 'false');
            panel.removeAttribute('hidden');
            panel.removeAttribute('data-nested-state');
            panel.removeAttribute('data-nested-swap');
            panel.removeAttribute('role');
            panel.removeAttribute('aria-modal');
        }

        #isDesktopNav() {
            // While a nested dialog owns the menu (search, or any panel on touch) the
            // desktop hover behaviour is off.
            return Boolean(this.menu) && !this.touchQuery.matches && !this.activeNested;
        }

        #desktopItemFor(node) {
            if (!(node instanceof Element)) return null;

            const item = node.closest('.header-menu--link-nested');
            if (item && this.menu.contains(item)) return item;

            const panel = node.closest('[data-link-menu][data-nested-state]');
            if (!panel) return null;

            const panelItem = panel.closest('.header-menu--link-nested');
            return panelItem && this.menu.contains(panelItem) ? panelItem : null;
        }

        #activeDesktopPanel() {
            return this.activeDesktopItem?.querySelector('[data-link-menu][data-nested-state]') ?? null;
        }

        #scheduleDesktopPanelClose() {
            this.#clearDesktopCloseTimer();
            this.#clearDesktopOpenTimer();

            this.desktopCloseTimer = setTimeout(() => {
                this.desktopCloseTimer = null;
                this.#requestDesktopPanel(null);
            }, DESKTOP_CLOSE_DELAY);
        }

        #clearDesktopCloseTimer() {
            if (!this.desktopCloseTimer) return;

            clearTimeout(this.desktopCloseTimer);
            this.desktopCloseTimer = null;
        }

        #scheduleDesktopPanelOpen(item) {
            if (this.pendingDesktopItem === item && this.desktopOpenTimer) return;

            this.#clearDesktopOpenTimer();
            this.pendingDesktopItem = item;

            this.desktopOpenTimer = setTimeout(() => {
                this.desktopOpenTimer = null;
                this.pendingDesktopItem = null;
                this.#requestDesktopPanel(item);
            }, DESKTOP_OPEN_DELAY);
        }

        #clearDesktopOpenTimer() {
            if (!this.desktopOpenTimer && !this.pendingDesktopItem) return;

            clearTimeout(this.desktopOpenTimer);
            this.desktopOpenTimer = null;
            this.pendingDesktopItem = null;
        }

        // Hover intent: delay the first open so a cursor crossing the bar does not
        // flash a dropdown. Once a menu is open, swaps stay immediate.
        #requestDesktopPanelFromPointer(item) {
            if (this.activeDesktopItem === item) {
                this.#clearDesktopOpenTimer();
                return;
            }

            if (this.activeDesktopItem) {
                this.#clearDesktopOpenTimer();
                this.#requestDesktopPanel(item);
                return;
            }

            this.#scheduleDesktopPanelOpen(item);
        }

        // Pointer and focus events raised inside a panel that is animating out belong to
        // the outgoing menu, so they must not pull the sheet back to it.
        #isTransitionalTarget(node) {
            if (!(node instanceof Element)) return false;

            const panel = node.closest('[data-link-menu]');
            return Boolean(panel) && panel.getAttribute('data-nested-state') !== 'open';
        }

        #isHovered(element) {
            return element instanceof Element && element.matches(':hover');
        }

        #requestDesktopPanel(item) {
            this.#clearDesktopOpenTimer();

            if (item) {
                this.#clearDesktopCloseTimer();
            }

            if (this.activeDesktopItem === item) return;

            this.#setDesktopPanel(item);
        }

        // The shared sheet means there is nothing to queue: the outgoing panel dissolves
        // in place while the incoming one is already live underneath it.
        #setDesktopPanel(item) {
            const outgoingPanel = this.activeDesktopItem?.querySelector('[data-link-menu]') ?? null;
            const incomingPanel = item?.querySelector('[data-link-menu]') ?? null;
            const isSwap = Boolean(outgoingPanel && incomingPanel && outgoingPanel !== incomingPanel);

            this.activeDesktopItem = item;

            if (outgoingPanel && outgoingPanel !== incomingPanel) {
                for (const trigger of this.#triggersForPanel(outgoingPanel)) {
                    trigger.setAttribute('aria-expanded', 'false');
                }

                this.#releaseDesktopFocus(outgoingPanel);

                outgoingPanel.toggleAttribute('data-nested-swap', isSwap);
                outgoingPanel.setAttribute('data-nested-state', 'closing');
                this.#scheduleClosingCleanup(outgoingPanel);
            }

            if (incomingPanel) {
                this.#cancelClosingCleanup(incomingPanel);
                this.#observePanelHeights();
                this.#initCarouselsInPanel(incomingPanel);

                incomingPanel.toggleAttribute('data-nested-swap', isSwap);
                incomingPanel.setAttribute('data-nested-state', 'open');

                for (const trigger of this.#triggersForPanel(incomingPanel)) {
                    trigger.setAttribute('aria-expanded', 'true');
                }
            }

            this.#syncSheetHeight();
            this.#syncScrollLock();
        }

        // Panels are laid out at all times on desktop (visibility, not display), so the
        // first read is cheap and the observer keeps it honest once Swiper, images and
        // fonts have settled.
        #observePanelHeights() {
            if (this.panelResizeObserver || !this.menu) return;

            this.panelResizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    this.panelHeights.set(entry.target, measureHeight(entry.target));
                }

                this.#syncSheetHeight();
            });

            const inners = this.menu.querySelectorAll('.header-menu--link-nested .header-menu--link-menu-inner');

            for (const inner of inners) {
                this.panelHeights.set(inner, measureHeight(inner));
                this.panelResizeObserver.observe(inner);
            }
        }

        #disconnectPanelResizeObserver() {
            this.panelResizeObserver?.disconnect();
            this.panelResizeObserver = null;
        }

        #syncSheetHeight() {
            if (this.touchQuery.matches) return;

            const panel = this.activeDesktopItem?.querySelector('[data-link-menu]');
            const inner = panel?.querySelector('.header-menu--link-menu-inner');
            const height = inner ? this.panelHeights.get(inner) ?? measureHeight(inner) : 0;

            this.style.setProperty('--header-menu-sheet-height', `${height}px`);
        }

        #scheduleClosingCleanup(panel) {
            this.#cancelClosingCleanup(panel);

            const inner = panel.querySelector('.header-menu--link-menu-inner');

            const settle = () => {
                this.#cancelClosingCleanup(panel);

                if (panel.getAttribute('data-nested-state') === 'closing') {
                    panel.removeAttribute('data-nested-state');
                    panel.removeAttribute('data-nested-swap');
                }

                this.#syncScrollLock();
            };

            // transitionend bubbles, so ignore anything raised by content inside the panel.
            const handleTransitionEnd = (event) => {
                if (event.target !== panel && event.target !== inner) return;

                settle();
            };

            this.closingPanels.set(panel, {
                handleTransitionEnd,
                timer: setTimeout(settle, transitionDuration(panel) + TRANSITION_BUFFER),
            });

            panel.addEventListener('transitionend', handleTransitionEnd);
        }

        #cancelClosingCleanup(panel) {
            const pending = this.closingPanels.get(panel);
            if (!pending) return;

            clearTimeout(pending.timer);
            panel.removeEventListener('transitionend', pending.handleTransitionEnd);
            this.closingPanels.delete(panel);
        }

        // Keeps focus somewhere predictable when a panel closes underneath it.
        #releaseDesktopFocus(panel) {
            const active = document.activeElement;
            if (!(active instanceof HTMLElement) || !panel.contains(active)) return;

            const trigger = this.#triggerForPanel(panel);
            if (!trigger) return;

            this.desktopFocusLock = true;
            trigger.focus({ preventScroll: true });
            this.desktopFocusLock = false;
        }

        #resetDesktopPanels() {
            for (const panel of [...this.closingPanels.keys()]) {
                this.#cancelClosingCleanup(panel);
            }

            this.#clearDesktopCloseTimer();
            this.#clearDesktopOpenTimer();
            this.activeDesktopItem = null;
            this.style.removeProperty('--header-menu-sheet-height');
            this.#syncScrollLock();
        }

        #triggersForPanel(panel) {
            const id = panel?.id;
            if (!id || !this.menu) return [];

            return this.menu.querySelectorAll(`[data-link-toggle][aria-controls="${CSS.escape(id)}"]`);
        }

        #bindSearchTriggers() {
            const panel = this.menu?.querySelector('[data-header-search-panel]');
            if (!panel?.id) return;

            for (const trigger of this.menu.querySelectorAll('[data-header-search-trigger]')) {
                trigger.setAttribute('aria-controls', panel.id);
            }
        }

        #panelForTrigger(trigger) {
            const id = trigger?.getAttribute('aria-controls');
            if (!id) return null;
            return this.menu.querySelector(`#${CSS.escape(id)}`);
        }

        #triggerForPanel(panel) {
            if (this.activeNestedTrigger && this.#panelForTrigger(this.activeNestedTrigger) === panel) {
                return this.activeNestedTrigger;
            }

            const id = panel?.id;
            if (!id) return null;

            return this.menu.querySelector(`[data-link-toggle][aria-controls="${CSS.escape(id)}"][aria-expanded="true"]`)
                ?? this.menu.querySelector(`[data-link-toggle][aria-controls="${CSS.escape(id)}"]`);
        }

        #isSearchPanel(panel) {
            return panel?.classList.contains('header-search--menu');
        }

        #isIOS() {
            return /iP(ad|hone|od)/.test(navigator.userAgent)
                || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        }

        #focusSearchInput(panel, { allowRetry = true } = {}) {
            const input = panel.querySelector('input[type="search"]');
            if (!input) return;

            const isTouch = this.touchQuery.matches;
            const isiOS = isTouch && this.#isIOS();

            const focusInput = ({ useProxy = false } = {}) => {
                if (this.activeNested !== panel) return true;

                if (useProxy && isiOS) {
                    // iOS will not open the keyboard for an input that was
                    // display:none / off-screen at tap time. A 1px proxy in
                    // the viewport claims the keyboard during the gesture,
                    // then focus moves to the real field.
                    const proxy = document.createElement('input');
                    proxy.type = 'search';
                    proxy.setAttribute('aria-hidden', 'true');
                    proxy.tabIndex = -1;
                    proxy.autocomplete = 'off';
                    proxy.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;border:0;padding:0;margin:0;pointer-events:none;';
                    document.body.appendChild(proxy);
                    proxy.focus();
                    input.focus();
                    proxy.remove();
                } else if (isTouch) {
                    input.focus();
                } else {
                    input.focus({ preventScroll: true });
                }

                return document.activeElement === input;
            };

            // The panel was display:none; force layout so WebKit treats the
            // input as visible before the user-gesture token expires.
            void panel.offsetHeight;

            if (focusInput({ useProxy: true }) || !allowRetry) return;

            panel.addEventListener('transitionend', (event) => {
                if (event.target === panel) focusInput();
            }, { once: true });

            const retryFocus = (attempt = 0) => {
                if (focusInput() || attempt >= 30) return;

                requestAnimationFrame(() => retryFocus(attempt + 1));
            };

            retryFocus();
        }

        #updateCompactMenuHeight(panel) {
            if (!this.touchQuery.matches || !this.menu || !panel) return;

            const inner = panel.querySelector('.header-search--menu-inner');
            const content = panel.querySelector('.header-search--content');
            const back = panel.querySelector('.header-menu--back');
            const nav = this.menu.querySelector('.header-menu--navigation');

            if (!inner || !content || !nav) return;

            const navStyles = getComputedStyle(nav);
            const innerStyles = getComputedStyle(inner);
            const gap = parseFloat(innerStyles.gap) || 0;
            const paddingV = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-padding-v')) || 0;
            const menuTop = parseFloat(this.menu.style.getPropertyValue('--header-menu-top')) || paddingV;

            const naturalHeight = content.scrollHeight
                + (back?.offsetHeight || 0)
                + gap
                + parseFloat(navStyles.paddingTop)
                + parseFloat(navStyles.paddingBottom);

            const viewportMax = window.innerHeight - menuTop - paddingV;
            const needsScroll = naturalHeight > viewportMax;
            const compactHeight = Math.min(Math.ceil(naturalHeight), Math.ceil(viewportMax));

            this.menu.style.setProperty('--header-menu-compact-height', `${compactHeight}px`);
            this.menu.setAttribute('data-menu-compact', '');
            this.menu.toggleAttribute('data-menu-compact-scroll', needsScroll);
        }

        #observeSearchPanelResize(panel) {
            this.#disconnectSearchResizeObserver();

            const content = panel.querySelector('.header-search--content');

            if (!content) return;

            this.searchResizeObserver = new ResizeObserver(() => {
                this.#updateCompactMenuHeight(panel);
            });
            this.searchResizeObserver.observe(content);
        }

        #disconnectSearchResizeObserver() {
            this.searchResizeObserver?.disconnect();
            this.searchResizeObserver = null;
        }

        #restoreCompactMenu() {
            this.#disconnectSearchResizeObserver();
            this.menu?.removeAttribute('data-menu-compact');
            this.menu?.removeAttribute('data-menu-compact-scroll');
            this.menu?.style.removeProperty('--header-menu-compact-height');
        }

        handlePanelReveal(event) {
            const nested = event.target.closest('.header-menu--link-nested, .header-menu--link-search');

            if (!nested || !this.menu?.contains(nested)) return;

            const panel = nested.querySelector('[data-link-menu]');

            if (panel) {
                this.#initCarouselsInPanel(panel);
            }
        }

        #initCarouselsInPanel(panel) {
            if (this.#isSearchPanel(panel)) {
                for (const carousel of panel.querySelectorAll('[data-search-suggestions]')) {
                    this.#initSearchSuggestionsCarousel(carousel);
                }

                return;
            }

            for (const carousel of panel.querySelectorAll('[data-link-cta]')) {
                this.#initCtaCarousel(carousel);
            }
        }

        async #initMobileLinksCarousel() {
            if (this.mobileLinksCarouselInitialized || !this.elMobileLinksCarousel) return;

            const slides = this.elMobileLinksCarousel.querySelectorAll('[data-mobile-links-slide]');
            this.elMobileLinksCarousel.toggleAttribute('hidden', slides.length === 0);

            if (slides.length === 0) {
                this.mobileLinksCarouselInitialized = true;
                return;
            }

            this.mobileLinksCarouselInitialized = true;

            const Swiper = await loadSwiper({ waitForLoad: false });
            const swiper = new Swiper(this.elMobileLinksCarousel, {
                enabled: slides.length > 2,
                loop: true,
                slidesPerView: 'auto',
                spaceBetween: 8,
                slidesOffsetBefore: 12,
                slidesOffsetAfter: 12,
                a11y: {
                    enabled: true,
                    prevSlideMessage: 'Previous link',
                    nextSlideMessage: 'Next link',
                },
                on: {
                    slideChange: (s) => {
                        s.updateSlides()
                    }
                }
            });

            this.elMobileLinksCarousel.setAttribute('data-swiper-ready', '');
        }

        async #initCtaCarousel(carousel) {
            if (this.initializedCarousels.has(carousel)) return;

            const slides = carousel.querySelectorAll('[data-link-cta-slide]');
            const next = carousel.parentNode.querySelector('[data-link-cta-nav="next"]');
            const prev = carousel.parentNode.querySelector('[data-link-cta-nav="prev"]');
            const current = carousel.parentNode.querySelector('[data-link-cta-current]');
            const total = carousel.parentNode.querySelector('[data-link-cta-total]');
            const pagination = carousel.parentNode.querySelector('[data-link-cta-pagination]');
            const desktopSlidesPerView = Number(carousel.dataset.linkCtaSlides) || 1;

            const enabled = slides.length > 1;
            !enabled && (next.parentNode.style.display = 'none');

            this.initializedCarousels.add(carousel);

            const updatePagination = (swiper) => {
                current.textContent = swiper.snapIndex + 1;
                total.textContent = swiper.snapGrid.length;
                pagination?.toggleAttribute('hidden', swiper.snapGrid.length <= 1);
            };

            const Swiper = await loadSwiper({ waitForLoad: false });
            const swiper = new Swiper(carousel, {
                enabled: enabled,
                loop: enabled,
                watchOverflow: true,
                slidesPerView: 1,
                slidesPerGroup: 1,
                spaceBetween: 12,
                navigation: {
                    nextEl: next,
                    prevEl: prev,
                },
                a11y: {
                    enabled: true,
                    prevSlideMessage: 'Previous CTA',
                    nextSlideMessage: 'Next CTA',
                },
                breakpoints: {
                    1024: {
                        slidesPerView: desktopSlidesPerView,
                        slidesPerGroup: desktopSlidesPerView,
                    },
                },
                on: {
                    init: updatePagination,
                    slideChange: updatePagination,
                    breakpoint: updatePagination,
                }
            });

            carousel.setAttribute('data-swiper-ready', '');
            this.ctaCarousels.push(swiper);
        }

        async #initSearchSuggestionsCarousel(carousel) {
            if (this.initializedCarousels.has(carousel)) return;

            const suggestions = carousel.closest('.header-search--suggestions');
            const slides = carousel.querySelectorAll('.swiper-slide');
            const nav = carousel.parentNode.querySelector('.header-search--suggestions-nav');
            const next = nav?.querySelector('[data-search-suggestions-nav="next"]');
            const prev = nav?.querySelector('[data-search-suggestions-nav="prev"]');

            if (slides.length === 0) {
                suggestions?.toggleAttribute('hidden', true);
                return;
            }

            suggestions?.toggleAttribute('hidden', false);

            const enabled = slides.length > 2;

            if (nav) {
                nav.toggleAttribute('hidden', !enabled);
            }

            this.initializedCarousels.add(carousel);

            const Swiper = await loadSwiper({ waitForLoad: false });
            const swiper = new Swiper(carousel, {
                enabled: enabled,
                loop: false,
                slidesPerView: 'auto',
                spaceBetween: 8,
                slidesOffsetBefore: 12,
                slidesOffsetAfter: 12,
                observer: true,
                observeParents: true,
                navigation: {
                    nextEl: next,
                    prevEl: prev,
                },
                a11y: {
                    enabled: true,
                    prevSlideMessage: 'Previous suggestion',
                    nextSlideMessage: 'Next suggestion',
                },
                breakpoints: {
                    768: {
                        spaceBetween: 10,
                    },
                    1024: {
                        slidesOffsetBefore: 0,
                    },
                },
            });

            carousel.setAttribute('data-swiper-ready', '');
            this.searchSuggestionsCarousels.push(swiper);
        }

        #updateSearchSuggestionsCarousels() {
            for (const swiper of this.searchSuggestionsCarousels) {
                swiper?.update();
            }
        }


    }

    const transitionDuration = (element) => {
        const styles = getComputedStyle(element);
        const durations = styles.transitionDuration.split(',');
        const delays = styles.transitionDelay.split(',');

        const toMs = (value = '0s') => {
            const time = parseFloat(value);
            if (Number.isNaN(time)) return 0;

            return value.trim().endsWith('ms') ? time : time * 1000;
        };

        let longest = 0;

        for (const [index, duration] of durations.entries()) {
            longest = Math.max(longest, toMs(duration) + toMs(delays[index]));
        }

        return longest;
    };

    // Rounded up so the sheet can never clip the last sub-pixel row of its content.
    const measureHeight = (element) => Math.ceil(element.getBoundingClientRect().height);

    const isVisible = (element) => {
        if (element.hidden) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        return element.offsetParent !== null || element.getClientRects().length > 0;
    };

    customElements.define('header-bar', HeaderBar);

    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;

        document.querySelector('header-bar')?.resetMenuState?.();
    });
}
